import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  WorkflowService,
  AssistantsService,
  CaseManagementService,
  ExportPreviewResponse,
  ExportableReference,
  ExportWarning,
  ExportWarningType,
} from "@/generated";
import { cn } from "@/lib/utils";
import { useSpotUser } from "@/utils/spotUser";
import { useExportEntity, EntityType } from "@/hooks/useExportEntity";
import { SpotToast } from "@/components/SpotToast";
import BaseDialog from "../Modals/BaseDialog";
import {
  WorkflowVersionContext,
  WorkflowVersionInput,
} from "../Form/WorkflowVersionField";
import {
  AssistantVersionContext,
  AssistantVersionInput,
} from "../Form/AssistantVersionField";
import { Alert, AlertDescription } from "../shadcn/alert";
import { Skeleton } from "../shadcn/skeleton";
import { Switch } from "../shadcn/switch";
import { NoxusIcon } from "../UI/NoxusIcon";
import { EntityDialogTitle } from "../Modals/EntityDialogTitle";
import {
  EntityReferenceRow,
  getIconForType,
  getSingularLabelForType,
  getUrlForEntity,
} from "../UI/EntityReferenceRow";
import { AlertCircle, ExternalLink } from "lucide-react";
import Link from "next/link";

const ENTITY_LABELS: Record<
  EntityType,
  { singular: string; displayName: string; icon: string }
> = {
  workflow: { singular: "flow", displayName: "flow", icon: "workflow" },
  assistant: { singular: "agent", displayName: "agent", icon: "coworker" },
  kb: { singular: "knowledge base", displayName: "knowledge base", icon: "kb" },
  inbox: { singular: "inbox", displayName: "inbox", icon: "inbox" },
};

interface ExportPreviewDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string;
  entityName: string;
  entityType: EntityType;
}

export function ExportPreviewDialog({
  isOpen,
  onOpenChange,
  entityId,
  entityName,
  entityType,
}: ExportPreviewDialogProps) {
  // Agents default to exporting every saved version (plus the deployments
  // pinned to them); flows keep the current-version default.
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    entityType === "assistant" ? "all" : "current"
  );
  const [includeDependencies, setIncludeDependencies] = useState(true);
  const { spotGroup } = useSpotUser();

  const labels = ENTITY_LABELS[entityType];
  const entityLabel = labels.singular;

  // useQuery (not useRemoteData) here because the queryFn branches by
  // entityType across three different generated services. useRemoteData
  // assumes a single queryFn keyed by a DATA_KEYS entry; modelling three
  // entity-typed previews under one key would be more contortion than
  // value for a modal that's open seconds at a time.
  const {
    data: previewData,
    isLoading: isLoadingPreview,
    error: previewError,
  } = useQuery({
    queryKey: ["export-preview", entityType, entityId, selectedVersionId],
    queryFn: async () => {
      const versionId =
        selectedVersionId === "current" ? undefined : selectedVersionId;

      if (entityType === "workflow") {
        const response = await WorkflowService.exportWorkflowPreview({
          path: {
            group_id: spotGroup.id,
            workflow_id: entityId,
          },
          query: versionId ? { version_id: versionId } : undefined,
        });
        return response.data as ExportPreviewResponse;
      } else if (entityType === "inbox") {
        const response = await CaseManagementService.exportInboxPreview({
          path: {
            group_id: spotGroup.id,
            inbox_id: entityId,
          },
        });
        return response.data as ExportPreviewResponse;
      } else {
        // Omitted version_id -> all versions (the backend default);
        // "current" is spelled "latest" on the wire.
        const assistantVersionId =
          selectedVersionId === "all"
            ? undefined
            : selectedVersionId === "current"
              ? "latest"
              : versionId;
        const response = await AssistantsService.exportAssistantPreview({
          path: {
            group_id: spotGroup.id,
            assistant_id: entityId,
          },
          query: assistantVersionId
            ? { version_id: assistantVersionId }
            : undefined,
        });
        return response.data as ExportPreviewResponse;
      }
    },
    enabled: isOpen && entityType !== "kb",
  });

  const allWarnings = previewData?.warnings || [];
  const warnings = allWarnings.filter(
    (w) => includeDependencies || w.type !== ExportWarningType.TABLE_TOO_LARGE
  );
  const entities = useMemo(
    () => previewData?.entities || [],
    [previewData?.entities]
  );
  const hasWarnings = warnings.length > 0;
  const exportBlocked = warnings.some(
    (w) => w.type === ExportWarningType.TABLE_TOO_LARGE
  );

  // Export mutation
  const exportMutation = useExportEntity({
    onSuccess: () => onOpenChange(false),
  });

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleExport = () => {
    exportMutation.mutate({
      entityId,
      entityName,
      entityType,
      selectedVersionId,
      // Bundle metadata (incl. the suggested-active hint) ships unconditionally
      // — that's the right default for cross-environment promotion and there's
      // no use case for an anonymous bundle.
      setActiveOnImport: true,
      includeDependencies,
    });
  };

  const isExporting = exportMutation.isPending;

  const entitiesByType = useMemo(() => {
    const grouped: Record<string, ExportableReference[]> = {};
    // One row per entity: versioned refs share the entity's reference (an
    // all-versions export carries one per saved version) and would render as
    // duplicate rows with colliding React keys.
    const seen = new Set<string>();
    entities.forEach((e) => {
      const key = `${e.type}-${e.reference}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (!grouped[e.type]) grouped[e.type] = [];
      grouped[e.type].push(e);
    });
    return grouped;
  }, [entities]);

  const hasOtherComponents = useMemo(() => {
    return entities.some((e) => e.reference !== entityId && e.type !== "file");
  }, [entities, entityId]);

  // An all-versions export lists one versioned ref per saved version; they
  // share the root's reference (so the components list skips them) and are
  // summarized as a count under the picker instead.
  const includedVersionCount = useMemo(
    () =>
      entities.filter(
        (e) => e.type === "assistant" && e.reference === entityId && !!e.version
      ).length,
    [entities, entityId]
  );

  const TitleComponent = () => {
    const mainEntity = entities.find((e) => e.reference === entityId);
    const defaultIcon =
      entityType === "workflow"
        ? "workflow"
        : entityType === "assistant"
          ? "coworker"
          : entityType === "inbox"
            ? "inbox"
            : "kb";
    // Cast to access icon field (will be in generated types after schema regeneration)
    const icon =
      (mainEntity as ExportableReference & { icon?: string })?.icon ||
      defaultIcon;
    return (
      <EntityDialogTitle action="Exporting" icon={icon} name={entityName} />
    );
  };

  const getDescription = () => {
    if (entityType === "kb") {
      return "Confirm the export details for this knowledge base.";
    }
    if (entityType === "inbox") {
      return "Confirm the export details for this inbox and its workflows.";
    }
    return "Select a version and confirm the export details.";
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={<TitleComponent />}
      description={getDescription()}
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "ghost",
            onClick: handleClose,
          },
        },
        {
          label:
            allWarnings.length > 0 && !exportBlocked
              ? "Export Anyway"
              : "Export",
          props: {
            onClick: handleExport,
            loading: isExporting,
            disabled: isLoadingPreview || exportBlocked,
          },
        },
      ]}
    >
      <div className="flex flex-col gap-6">
        {/* Version selection for workflows/assistants - always shown */}
        {(entityType === "workflow" || entityType === "assistant") && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Version to export</span>
            {entityType === "workflow" && (
              <WorkflowVersionContext.Provider value={entityId}>
                <WorkflowVersionInput
                  value={selectedVersionId || undefined}
                  onChange={setSelectedVersionId}
                  allowCurrentVersion
                  disablePortal
                />
              </WorkflowVersionContext.Provider>
            )}
            {entityType === "assistant" && (
              <AssistantVersionContext.Provider value={entityId}>
                <AssistantVersionInput
                  value={selectedVersionId || undefined}
                  onChange={setSelectedVersionId}
                  allowCurrentVersion
                  allowAllVersions
                  disablePortal
                />
              </AssistantVersionContext.Provider>
            )}
            {entityType === "assistant" &&
              selectedVersionId === "all" &&
              includedVersionCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  Includes the current definition and all {includedVersionCount}{" "}
                  saved version
                  {includedVersionCount === 1 ? "" : "s"}.
                </span>
              )}
          </div>
        )}

        {isLoadingPreview ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-24" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3 rounded-lg p-2">
                <Skeleton className="h-6 w-6 rounded" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex items-center gap-3 rounded-lg p-2">
                <Skeleton className="h-6 w-6 rounded" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
          </div>
        ) : previewError ? (
          <p className="text-sm text-muted-foreground leading-normal">
            Could not load export preview. You can still try to export.
          </p>
        ) : (
          <>
            {/* Warnings section */}
            {hasWarnings && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Warnings</span>
                <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto">
                  {warnings.map((warning: ExportWarning, index: number) => (
                    <WarningAlert
                      key={`${warning.identifier}-${index}`}
                      warning={warning}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Dependency toggle + the components it would pull in */}
            {hasOtherComponents && (
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      Include dependencies
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Bundle the referenced components below into the .nx so it
                      imports standalone. Off exports only this {entityLabel}.
                    </span>
                  </div>
                  <Switch
                    checked={includeDependencies}
                    onCheckedChange={setIncludeDependencies}
                    aria-label="Include dependencies in export"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">
                    {includeDependencies
                      ? "Other components included"
                      : "Other components (not included)"}
                  </span>
                  <div
                    className={cn(
                      // rows carry p-2 themselves; any extra gap here makes
                      // row-to-row spacing larger than header-to-row spacing
                      "flex flex-col max-h-[250px] overflow-y-auto",
                      !includeDependencies && "opacity-50"
                    )}
                  >
                    {Object.entries(entitiesByType).map(([type, items]) =>
                      items.map((item) => {
                        return (
                          <ItemToExport
                            key={item.reference}
                            item={item}
                            entityId={entityId}
                            type={type}
                          />
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </BaseDialog>
  );
}

const ItemToExport = ({
  item,
  entityId,
  type,
}: {
  item: ExportableReference;
  entityId: string;
  type: string;
}) => {
  // Skip the main entity being exported and files
  if (item.reference === entityId || type === "file") return null;

  const url = getUrlForEntity(type, item.reference);
  // Cast to access name/icon fields (will be in generated types after schema regeneration)
  const itemWithExtras = item as ExportableReference & {
    name?: string;
    icon?: string;
  };
  const displayName = itemWithExtras.name || `${getSingularLabelForType(type)}`;
  // Seeing a secret listed under "will be exported" reads as the value leaving
  // the workspace. Only its name travels, so say so where it's listed.
  const typeLabel =
    type === "secret"
      ? "Secret · name only, value is not exported"
      : type === "connection"
        ? "Connection · identity only, reconnect after import"
        : getSingularLabelForType(type);
  // Use the icon from the item if available, otherwise fall back to type default
  const itemIcon = itemWithExtras.icon || getIconForType(type);

  return (
    <EntityReferenceRow
      icon={itemIcon}
      name={displayName}
      typeLabel={typeLabel}
      href={url}
    />
  );
};

const WarningAlert = ({ warning }: { warning: ExportWarning }) => {
  const getIconForWarningType = (warningType: string): string => {
    const type = warningType.toLowerCase();
    if (type.includes("kb")) return "kb";
    if (type.includes("file")) return "file";
    if (type.includes("trigger")) return "workflow"; // Triggers are on workflows
    if (type.includes("app_deployment")) return "coworker";
    return "workflow";
  };

  const warningIcon = warning.icon || getIconForWarningType(warning.type);
  // Determine entity type from warning type for linking
  const warningType = warning.type.toLowerCase();
  let warningEntityType: string | null = null;
  if (warningType.includes("kb")) {
    warningEntityType = "kb";
  } else if (warningType.includes("trigger")) {
    warningEntityType = "workflow";
  } else if (warningType.includes("app_deployment")) {
    warningEntityType = "assistant";
  }
  const warningUrl =
    warningEntityType && warning.identifier !== "workspace"
      ? getUrlForEntity(warningEntityType, warning.identifier)
      : null;

  return (
    <Alert variant="warning" className="py-2 flex items-center">
      <NoxusIcon icon={warningIcon} size="md" />
      <AlertDescription className="ml-2">
        {warningUrl ? (
          <Link
            href={warningUrl}
            target="_blank"
            className="font-medium hover:underline inline-flex items-center gap-1 me-1.5"
          >
            {warning.name || warning.identifier}
            <ExternalLink
              size={12}
              className="text-muted-foreground"
              color="var(--color-yellow-700)"
            />
          </Link>
        ) : (
          <span className="font-medium me-1.5">
            {warning.name || warning.identifier}
          </span>
        )}
        <span className="text-yellow-700">{warning.message}</span>
      </AlertDescription>
    </Alert>
  );
};
